import { PurpleColourId } from '../../../../base-styles/diagram-colours';
import { Colours } from '../../../../design-system/colours';
import { holdsActiveCustomModel } from '../../../../functions/client-model/client-node/holds-active-custom-model';
import { holdsCustomModel } from '../../../../functions/client-model/client-node/holds-custom-model';
import { holdsInitialisedUtilisationTE } from '../../../../functions/client-model/client-node/holds-initialised-utilisation-te';
import { isLeafNode } from '../../../../functions/client-model/client-node/is-leaf-node';
import { transitionElementToSector } from '../../../../functions/transition-target-to-sector';
import { stringToSector } from '../../../../functions/transition-targets/string-to-sector';
import { ClientNode } from '../../../../model/client-model/client-node';
import { Sector } from '../../../../model/sector';
import { groupColourToSector } from '../../../../modules/diagram/components/tree-diagram/group-color-to-sector';
import { getChildren } from '../../../../public/dashboard/shared/functions/get-children';
import { ClientBoardContainer } from '../../../../public/dashboard/shared/model/client-board-container';
import { DashboardColours } from '../../../../public/dashboard/shared/model/dashboard-colours';
import { TransitionTargetSector } from '../../../editor-targets-list/components/board-targets/sectors';

export function colourForNodeV5(board: ClientBoardContainer, node: ClientNode): DashboardColours {
  if (node.nodeProperties.groupColour) {
    return groupColourV5(node.nodeProperties.groupColour);
  }

  if (isLeafNode(node)) {
    return _colourForLeafNode(node);
  }

  return _getColourFromChildren(board, node) ?? _colourForNode();
}

export function groupColourV5(groupColour: string): DashboardColours {
  const colourName = _groupColourToColourName(groupColour);
  return _colourForNode(colourName);
}

function _groupColourToColourName(groupColour: string): string {
  if (groupColour === PurpleColourId) {
    //Colours will not be tied to sectors in the future, so we will never have a sector for purple.
    return 'purple';
  }
  return _sectorToColourName(groupColourToSector(Sector.None, groupColour));
}

function _colourForLeafNode(node: ClientNode): DashboardColours {
  let sector: Sector | undefined;
  if (holdsCustomModel(node)) {
    sector = stringToSector(
      node.nodeProperties.customTargetModelData
        ? node.nodeProperties.customTargetModelData.sector
        : TransitionTargetSector.TRANSPORT
    );
  } else if (holdsInitialisedUtilisationTE(node)) {
    sector = transitionElementToSector(node.nodeProperties.transitionElement);
  }

  return _colourForNode(_sectorToColourName(sector));
}

function _sectorToColourName(sector?: Sector): string {
  switch (sector) {
    case Sector.Transport:
      return 'green';
    case Sector.Agriculture:
      return 'orange';
    case Sector.Energy:
      return 'yellow';
    case Sector.Industry:
      return 'blue';
    case Sector.Other:
      return 'pink';
    case Sector.None:
      return 'teal';
    default:
      return 'grey';
  }
}

function _colourForNode(colour?: string): DashboardColours {
  switch (colour) {
    case 'green':
      return {
        main: Colours.transport400,
        dark: Colours.transport500,
        light: Colours.transport300,
        headingColour: Colours.transport400,
      };
    case 'blue':
      return {
        main: Colours.industry400,
        dark: Colours.industry500,
        light: Colours.industry300,
        headingColour: Colours.industry400,
      };
    case 'orange':
      return {
        main: Colours.agriculture400,
        dark: Colours.agriculture500,
        light: Colours.agriculture300,
        headingColour: Colours.agriculture400,
      };
    case 'yellow':
      return {
        main: Colours.energy400,
        dark: Colours.energy500,
        light: Colours.energy300,
        headingColour: Colours.energy400,
      };
    case 'pink':
      return {
        main: Colours.other400,
        dark: Colours.other500,
        light: Colours.other300,
        headingColour: Colours.other400,
      };
    case 'teal':
      return {
        main: Colours.waste400,
        dark: Colours.waste500,
        light: Colours.waste300,
        headingColour: Colours.waste400,
      };
    case 'purple':
      return {
        main: Colours.purple400,
        dark: Colours.purple500,
        light: Colours.purple300,
        headingColour: Colours.purple400,
      };

    default:
      return {
        main: Colours.grey400,
        dark: Colours.grey500,
        light: Colours.grey300,
        headingColour: Colours.grey400,
      };
  }
}

function _getColourFromChildren(board: ClientBoardContainer, node: ClientNode): DashboardColours | undefined {
  let colours: DashboardColours[] = getChildren(board, node).reduce((childColours: DashboardColours[], childNode) => {
    const canContributeColourToParent =
      !isLeafNode(childNode) || holdsInitialisedUtilisationTE(childNode) || holdsActiveCustomModel(childNode);
    if (canContributeColourToParent) {
      childColours.push(colourForNodeV5(board, childNode));
    }
    return childColours;
  }, []);

  if (colours.length) {
    const firstColour = colours[0];
    const firstMainColour = firstColour.main;
    const mainColours = colours.map((c) => c.main);
    if (mainColours.every((s) => s === firstMainColour)) return firstColour;
  }
}
